import React, { useEffect, useMemo, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { ChangeStatusButton } from "Components/AppGrid/shipeezi-new-grid/GridActions";
import BadgeStatus from "Components/Generic/BadgeStatus";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { format } from "date-fns";
import { AttachSquare, Message, Note, Notification, ShieldTick, Sms } from "iconsax-react";
import { useTranslation } from "react-i18next";
import { FiEdit, FiEye, FiCopy } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import { useAppState } from "Context/StateContext";
import { Filters, Searches } from "Models/shipeezi.table";
import { Status } from "Util/enums";
import { handlesGrid } from "Util/handlesGrid";
import useStyles from "./styles";
import TemplateViewDrawer, { TemplateViewDrawerHandles } from "./view";
import { NotificationTemplate } from "Models/notification.template";
import CommunicationCentreService from "Services/communication.centre.service";
import { formatDateWithUserPref } from "Util/dateFormaterPreference";

// Constants for caching
const CACHE_TIME = 5 * 60 * 1000; // 5 minutes
const STALE_TIME = 2 * 60 * 1000; // 2 minutes

const CommunicationCentreTab = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser, userPermissions } = useAppState();
  const history = useHistory();
  const templateDrawerRef = useRef<TemplateViewDrawerHandles>(null);
  const [communicationPermission, setCommunicationPermission] = useState<{
    edit: boolean;
    create: boolean;
  }>({
    edit: false,
    create: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | undefined>();
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalItems, setTotalItems] = useState<number>(0);

  const { handleFilter, handleSearch, handlePageSize } = handlesGrid({
    setFilters,
    setSearches,
    setPage,
    setPageSize,
  });

  const {
    data: templatesData,
    isFetching: isLoadingTemplates,
    refetch: refetchTemplates,
    error: templatesError,
  } = useQuery(
    ["communication-centre.list", page, pageSize, filters, searches, currentUser?.companyId],
    async () => {
      const response = await CommunicationCentreService.getAll({
        page,
        pageSize,
        companyId: currentUser?.companyId,
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      });

      console.log("found data: ", response.data?.length);
      if (response && response.data) return response.data;

      return [];
    },
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
      onError: (error) => {
        console.error("Error fetching templates:", error);
        NotificationToast.error(translate("errorLoadingTemplates"));
      },
    }
  );

  const { data: totalCount, error: countError } = useQuery(
    ["communication-centre.count", filters, searches, currentUser?.companyId],
    async () => {
      const response = await CommunicationCentreService.count({
        companyId: currentUser?.companyId,
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      });

      return response?.data || 0;
    },
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
      onSuccess: (count) => {
        setTotalItems(count);
      },
      onError: (error) => {
        console.error("Error fetching templates count:", error);
        setTotalItems(0);
      },
    }
  );

  const statusOrder = (status?: string) => {
    const normalizedStatus = status?.toLowerCase();
    if (normalizedStatus === Status.ACTIVE.toLowerCase()) return 0;
    if (normalizedStatus === Status.INACTIVE.toLowerCase()) return 1;
    return 2;
  };

  const sortTemplates = (items: NotificationTemplate[]) => {
    return [...items].sort((a, b) => {
      const statusDiff = statusOrder(a.status) - statusOrder(b.status);
      if (statusDiff !== 0) return statusDiff;

      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return dateB - dateA;
    });
  };

  const sortedTemplates = useMemo(() => {
    if (!templatesData) return [];
    return sortTemplates(templatesData);
  }, [templatesData]);

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setCommunicationPermission({
        edit: true,
        create: true,
      });
    }
  }, [userPermissions]);

  const handleCurrentPage = (numPage: number) => {
    document.getElementById("content")?.scrollTo({ top: 0, behavior: "smooth" });
    setPage(numPage - 1);
  };

  const badgeStatus = (status: string) => {
    return <BadgeStatus value={translate(status)} />;
  };

  const renderDefaultBadge = (isDefault: boolean) => {
    if (isDefault) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <BadgeStatus
            value="Default"
            useExternalColor={true}
            backgroundColor="#E6EEF6"
            textColor="#004D95"
          />
        </div>
      );
    }

    return null;
  };

  const renderShieldBadge = (isSystemTemplate: boolean) => {
    if (isSystemTemplate) {
      return (
        <div
          style={{
            display: "flex",
            width: "32px",
            height: "32px",
            padding: "10px",
            justifyContent: "center",
            alignItems: "center",
            gap: "4px",
            flexShrink: 0,
            borderRadius: "100px",
            background: "var(--Accent-Blue-50, #EFF8FF)",
          }}
        >
          <ShieldTick
            size="24"
            color="#2E90FA"
            variant="Bold"
            style={{ width: "24px", height: "24px", minWidth: "24px" }}
          />
        </div>
      );
    }

    return null;
  };

  const renderCopyBadge = (parentId?: number) => {
    if (!parentId) {
      return null;
    }

    return (
      <button
        onClick={() => handleOpenOriginalTemplate(parentId)}
        style={{
          display: "flex",
          width: "32px",
          height: "32px",
          padding: "10px",
          justifyContent: "center",
          alignItems: "center",
          gap: "4px",
          flexShrink: 0,
          borderRadius: "100px",
          background: "#F9FAFB",
          border: "none",
          cursor: "pointer",
        }}
      >
        <Note
          size="24"
          color="#667085"
          variant="Outline"
          style={{ width: "24px", height: "24px", minWidth: "24px" }}
        />
      </button>
    );
  };

  const renderContentIcons = (item: NotificationTemplate) => {
    const icons = [];
    const iconStyle = {
      color: "#0BA5EC",
      fontSize: "20px",
    };

    const isEmail = item.notificationTypes?.includes("email");
    const isSms = item.notificationTypes?.includes("sms");
    const isPush = item.notificationTypes?.includes("push");

    if (isEmail) {
      icons.push(<Message size={iconStyle.fontSize} color={iconStyle.color} variant="Bold" />);
    }
    if (isSms) {
      icons.push(<Sms size={iconStyle.fontSize} color={iconStyle.color} variant="Bold" />);
    }
    if (isPush) {
      icons.push(<Notification size={iconStyle.fontSize} color={iconStyle.color} variant="Bold" />);
    }

    if (item.notificationAttachments && item.notificationAttachments.length > 0) {
      icons.push(<AttachSquare size={iconStyle.fontSize} color={iconStyle.color} variant="Bold" />);
    }
    return <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>{icons}</div>;
  };

  const editTemplate = (index: number) => {
    const templateToUpdate = sortedTemplates.find(
      (template) => Number(template.id) === Number(index)
    );

    if (templateToUpdate) {
      history.push("/app/template-create", {
        editTemplate: templateToUpdate,
        isEdit: true,
        tabKey: "communicationcentre",
      });
    }
  };

  const handleOpenOriginalTemplate = (parentId: number) => {
    const originalTemplate = sortedTemplates.find(
      (template) => Number(template.id) === Number(parentId)
    );

    if (originalTemplate) {
      history.push("/app/template-create", {
        editTemplate: originalTemplate,
        isEdit: true,
        tabKey: "communicationcentre",
      });
    }
  };

  const viewTemplate = (index: number) => {
    const template = sortedTemplates.find((template) => Number(template.id) === Number(index));

    if (template) {
      console.log("View template:", template);
      setSelectedTemplate(template);
      templateDrawerRef.current?.openDrawer();
    }
  };

  const copyTemplate = (index: number) => {
    const templateToCopy = sortedTemplates.find(
      (template) => Number(template.id) === Number(index)
    );

    if (templateToCopy) {
      history.push("/app/template-create", {
        duplicateTemplate: templateToCopy,
        isDuplicate: true,
        tabKey: "communicationcentre",
      });
    }
  };

  const { mutate: updateTemplateStatus } = useMutation(
    ["communication-centre.update.status"],
    ({ id, template }: { id: number; template: NotificationTemplate }) => {
      return CommunicationCentreService.update(id, template);
    },
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        refetchTemplates();
      },
      onError: (error) => {
        console.error("Error updating template status:", error);
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const activeOrInactiveTemplate = (id: string | number, template: NotificationTemplate) => {
    if (template) {
      const templateUpdate: NotificationTemplate = {
        ...template,
        status: template.status === Status.INACTIVE ? Status.ACTIVE : Status.INACTIVE,
      };

      if (templateUpdate && templateUpdate?.id) {
        updateTemplateStatus({ id: templateUpdate.id, template: templateUpdate });
      }
    }
  };

  const handleCreateTemplate = () => {
    history.push("/app/template-create", { tabKey: "communicationcentre" });
  };

  useEffect(() => {
    setIsLoading(isLoadingTemplates);
  }, [isLoadingTemplates]);

  const columns: Column[] = [
    {
      name: "name",
      title: translate("name"),
      formatComponent: (value: string, row: NotificationTemplate) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <span>{value}</span>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {row.parentId
              ? renderCopyBadge(row.parentId)
              : renderShieldBadge(row.systemTemplate || false)}
            {renderDefaultBadge(row.default || false)}
          </div>
        </div>
      ),
    },
    {
      name: "subject",
      title: translate("subject"),
    },
    {
      name: "content",
      title: translate("content"),
      formatComponent: (value: string, row: NotificationTemplate) => renderContentIcons(row),
    },
    {
      name: "availableIn",
      title: translate("availableIn"),
      formatComponent: (value: string[]) => value?.join(" / "),
    },
    {
      name: "automation",
      title: translate("automation"),
      formatComponent: (value: boolean, row: NotificationTemplate) => {
        const triggersFromArray = Array.isArray(row.notificationTriggers)
          ? row.notificationTriggers.length
          : 0;
        return triggersFromArray > 0 ? translate("yes") : translate("no");
      },
    },
    {
      name: "updatedAt",
      title: translate("lastUpdate"),
      // formatComponent: (value: string) => format(new Date(value), "MMM dd, yyyy HH:mm:ss"),
      formatComponent: (value: string) =>
        value &&
        currentUser?.preferences && (
          <span>{formatDateWithUserPref(value, currentUser.preferences)}</span>
        ),
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (value: string) => badgeStatus(value),
    },
    {
      name: "action",
      title: translate("actions"),
      formatComponent: (value: string, row: NotificationTemplate) => (
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <Tooltip title={translate("changeStatus") || "Change status"} placement="top">
            <div style={{ cursor: "pointer" }}>
              <ChangeStatusButton
                onClick={() => activeOrInactiveTemplate(Number(row.id), row)}
                color="#475467"
                size={18}
              />
            </div>
          </Tooltip>
          <Tooltip title={translate("view")} placement="top">
            <div>
              <FiEye
                size={18}
                color="#475467"
                style={{ cursor: "pointer" }}
                onClick={() => viewTemplate(Number(row.id))}
              />
            </div>
          </Tooltip>
          {!row.systemTemplate && (
            <Tooltip title={translate("edit")} placement="top">
              <div>
                <FiEdit
                  size={18}
                  color="#475467"
                  style={{ cursor: "pointer" }}
                  onClick={() => editTemplate(Number(row.id))}
                />
              </div>
            </Tooltip>
          )}
          <Tooltip title={translate("duplicate")} placement="top">
            <div>
              <FiCopy
                size={18}
                color="#475467"
                style={{ cursor: "pointer" }}
                onClick={() => copyTemplate(Number(row.id))}
              />
            </div>
          </Tooltip>
        </div>
      ),
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleNewRecord"],
    },
    handleNewRecord: {
      text: translate("create"),
      action: handleCreateTemplate,
      tooltip: translate("create"),
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          options: [
            { label: translate("active"), value: "active" },
            { label: translate("inactive"), value: "inactive" },
          ],
        },
        {
          name: translate("automation"),
          options: [
            { label: translate("yes"), value: "true" },
            { label: translate("no"), value: "false" },
          ],
        },
      ],
      onChange: handleFilter,
    },
    handleSearch: {
      onSearch: handleSearch,
    },
  };

  return (
    <div className={classes.generalWrapper}>
      <ShipeeziNewGrid
        namePage={translate("items")}
        columns={columns}
        data={sortedTemplates}
        keyExtractor={(data) => data.id}
        totalItems={totalItems}
        loadingData={isLoadingTemplates}
        emptyData={translate("noTemplatesFound")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={handlePageSize}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        generalActions={generalActions}
        showFooterGrid={true}
        alwaysShowFooter={true}
      />

      <TemplateViewDrawer
        ref={templateDrawerRef}
        data={selectedTemplate}
        onClose={() => setSelectedTemplate(undefined)}
      />
    </div>
  );
};

export default CommunicationCentreTab;
